import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, FileText, Video, File, Book, Calendar, Eye, Edit, Trash2, Search, Tag, DollarSign } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter, 
  DialogTrigger,
  DialogDescription
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Course, CourseContent, CourseType, CourseSubtype, ContentType } from "@/types/courseTypes";
import { 
  createCourse, 
  getAllCourses, 
  deleteCourse, 
  addContentToCourse, 
  deleteContentFromCourse, 
  updateCourse 
} from "@/utils/firestoreService";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const CourseManagement = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"practical" | "individual">("practical");
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddCourseDialogOpen, setIsAddCourseDialogOpen] = useState(false);
  const [isAddContentDialogOpen, setIsAddContentDialogOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState<Course[]>([]);
  const [deleteCourseId, setDeleteCourseId] = useState<string | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const [newCourse, setNewCourse] = useState<Partial<Course>>({
    title: "",
    description: "",
    type: "practical",
    subtype: "practical",
    price: 0,
    pricePerHour: 0,
    duration: 0,
    totalHours: 0,
    published: false,
    contents: [],
  });

  const [newContent, setNewContent] = useState<Partial<CourseContent>>({
    title: "",
    description: "",
    type: "text",
    content: "",
    url: ""
  });

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setLoading(true);
        const fetchedCourses = await getAllCourses();
        setCourses(fetchedCourses);
      } catch (error) {
        console.error("Error fetching courses:", error);
        toast({
          title: "Error",
          description: "Failed to load courses. Please try again.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, [toast]);

  const filteredCourses = courses.filter(
    (course) =>
      course.subtype === activeTab &&
      (course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
       course.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleCourseInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    setNewCourse({
      ...newCourse,
      [name]: type === "number" ? parseFloat(value) : value,
    });
  };

  const handleCourseSelectChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewCourse((prev) => ({
      ...prev,
      [name]: value,
      // Reset subtype when type changes
      ...(name === "type" && { subtype: value === "practical" ? "practical" : "individual" }),
    }));
  };

  const handleOpenAddCourseDialog = () => {
    setNewCourse({
      title: "",
      description: "",
      type: "practical",
      subtype: activeTab,
      price: activeTab === "individual" ? undefined : 199.99,
      pricePerHour: activeTab === "individual" ? 45.99 : undefined,
      duration: activeTab === "individual" ? undefined : 10,
      published: true,
      contents: []
    });
    setIsAddCourseDialogOpen(true);
  };

  const handleContentInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewContent({
      ...newContent,
      [name]: value,
    });
  };

  const handleContentSelectChange = (value: string) => {
    setNewContent({
      ...newContent,
      type: value as ContentType,
      content: value === "text" ? newContent.content : "",
      url: value !== "text" ? newContent.url : "",
    });
  };

  const handleAddCourse = async () => {
    if (!newCourse.title || !newCourse.description || 
        (newCourse.subtype === "individual" && !newCourse.pricePerHour) ||
        (newCourse.subtype === "practical" && !newCourse.price)) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsCreating(true);
      
      const courseToSave = {
        ...newCourse,
        type: "practical" as CourseType,
        subtype: activeTab,
        published: true, // Always set to true for new courses
        // Remove price and duration for individual lessons
        ...(activeTab === "individual" 
          ? { 
              pricePerHour: Number(newCourse.pricePerHour),
              price: undefined,
              duration: undefined,
              totalHours: undefined
            }
          : { 
              price: Number(newCourse.price),
              pricePerHour: undefined,
              duration: Number(newCourse.duration),
              totalHours: Number(newCourse.duration) // Set totalHours equal to duration for practical courses
            }
        )
      };

      Object.keys(courseToSave).forEach(key => 
        courseToSave[key] === undefined && delete courseToSave[key]
      );

      let updatedCourse;
      
      if (selectedCourse) {
        // Update existing course
        await updateCourse(selectedCourse.id, courseToSave);
        updatedCourse = {
          ...selectedCourse,
          ...courseToSave,
          updatedAt: new Date().toISOString()
        };
        setCourses(courses.map(course => 
          course.id === selectedCourse.id ? updatedCourse : course
        ));
        toast({
          title: "Success",
          description: "Course updated successfully",
        });
      } else {
        // Create new course
        const createdCourse = await createCourse(courseToSave as Omit<Course, 'id'>);
        setCourses([...courses, createdCourse]);
        toast({
          title: "Success",
          description: "Course added successfully",
        });
      }
      
      // Reset form
      setNewCourse({
        title: "",
        description: "",
        type: "practical",
        subtype: activeTab,
        price: activeTab === "individual" ? undefined : 199.99,
        pricePerHour: activeTab === "individual" ? 45.99 : undefined,
        duration: activeTab === "individual" ? undefined : 10,
        published: true,
        contents: []
      });
      setSelectedCourse(null);
      setIsAddCourseDialogOpen(false);
      
    } catch (error) {
      console.error("Error saving course:", error);
      toast({
        title: "Error",
        description: `Failed to ${selectedCourse ? 'update' : 'add'} course. Please try again.`,
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleAddContent = async () => {
    if (!selectedCourse) {
      toast({
        title: "Error",
        description: "No course selected",
        variant: "destructive",
      });
      return;
    }

    if (!newContent.title || !newContent.description) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    if (newContent.type !== "text" && !newContent.url) {
      toast({
        title: "Error",
        description: "Please provide a URL for this content type",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);
      
      const contentToAdd = newContent as Omit<CourseContent, 'id' | 'createdAt' | 'updatedAt'>;
      const addedContent = await addContentToCourse(selectedCourse.id, contentToAdd);
      
      const updatedCourses = courses.map(course => 
        course.id === selectedCourse.id 
          ? { 
              ...course, 
              contents: [...(course.contents || []), addedContent],
              updatedAt: new Date().toISOString()
            } 
          : course
      );
      
      setCourses(updatedCourses);
      
      setNewContent({
        title: "",
        description: "",
        type: "text",
        content: "",
        url: ""
      });
      
      setIsAddContentDialogOpen(false);
      
      toast({
        title: "Success",
        description: "Content added successfully",
      });
    } catch (error) {
      console.error("Error adding content:", error);
      toast({
        title: "Error",
        description: "Failed to add content. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditCourse = async (course: Course) => {
    setSelectedCourse(course);
    setNewCourse({
      title: course.title,
      description: course.description,
      type: "practical",
      subtype: course.subtype,
      price: course.price,
      pricePerHour: course.pricePerHour,
      duration: course.duration,
      totalHours: course.totalHours,
      published: course.published,
      thumbnail: course.thumbnail,
    });
    setIsAddCourseDialogOpen(true);
  };

  const handleDeleteCourse = async (courseId: string) => {
    setDeleteCourseId(courseId);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteCourse = async () => {
    if (!deleteCourseId) return;
    
    try {
      setLoading(true);
      await deleteCourse(deleteCourseId);
      setCourses(courses.filter(course => course.id !== deleteCourseId));
      toast({
        title: "Success",
        description: "Course deleted successfully",
      });
    } catch (error) {
      console.error("Error deleting course:", error);
      toast({
        title: "Error",
        description: "Failed to delete course. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setIsDeleteDialogOpen(false);
      setDeleteCourseId(null);
    }
  };

  const handleTogglePublishCourse = async (courseId: string, currentStatus: boolean) => {
    try {
      setLoading(true);
      
      await updateCourse(courseId, { published: !currentStatus });
      
      const updatedCourses = courses.map(course => 
        course.id === courseId 
          ? { ...course, published: !currentStatus, updatedAt: new Date().toISOString() } 
          : course
      );
      
      setCourses(updatedCourses);
      
      toast({
        title: "Success",
        description: `Course ${currentStatus ? 'unpublished' : 'published'} successfully`,
      });
    } catch (error) {
      console.error("Error toggling course publish status:", error);
      toast({
        title: "Error",
        description: "Failed to update course. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getContentTypeIcon = (type: ContentType) => {
    switch (type) {
      case "text":
        return <FileText className="h-4 w-4 text-blue-500" />;
      case "video":
        return <Video className="h-4 w-4 text-red-500" />;
      case "file":
        return <File className="h-4 w-4 text-green-500" />;
      case "quiz":
        return <FileText className="h-4 w-4 text-purple-500" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  return (
    <DashboardLayout role="admin">
      <div className="mb-8 space-y-4">
        <h1 className="text-3xl font-bold text-gray-900">Course Management</h1>
        <p className="text-gray-600">
          Create and manage theory and practical driving courses.
        </p>
      </div>

      <Tabs defaultValue="practical" className="space-y-6" onValueChange={(value) => setActiveTab(value as "practical" | "individual")}>
        <TabsList>
          <TabsTrigger value="practical">Practical Courses</TabsTrigger>
          <TabsTrigger value="individual">Individual Courses</TabsTrigger>
        </TabsList>

        <TabsContent value="practical" className="space-y-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center w-full max-w-sm space-x-2">
              <Input
                placeholder="Search courses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1"
              />
              <Button variant="outline" size="icon">
                <Search className="h-4 w-4" />
              </Button>
            </div>

            <Dialog open={isAddCourseDialogOpen} onOpenChange={setIsAddCourseDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={handleOpenAddCourseDialog}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add {activeTab === "individual" ? "Individual" : "Practical"} Course
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {selectedCourse ? 'Edit' : 'Add'} {activeTab === "individual" ? "Individual" : "Practical"} Course
                  </DialogTitle>
                  <DialogDescription>
                    {selectedCourse 
                      ? `Update the details for this ${activeTab === "individual" ? "individual" : "practical"} course.`
                      : `Create a new ${activeTab === "individual" ? "individual" : "practical"} course. Fill in the required fields marked with an asterisk (*).`
                    }
                  </DialogDescription>
                </DialogHeader>
                
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="title">Title*</Label>
                      <Input
                        id="title"
                        name="title"
                        value={newCourse.title}
                        onChange={handleCourseInputChange}
                        placeholder={activeTab === "individual" ? "Individual Driving Lesson" : "Beginner Practical Driving"}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="type">Course Type</Label>
                      <Select
                        name="type"
                        value="practical"
                        onValueChange={(value) => handleCourseSelectChange({ target: { name: "type", value: "practical" } } as any)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select course type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="practical">Practical</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Description*</Label>
                    <Textarea
                      id="description"
                      name="description"
                      value={newCourse.description}
                      onChange={handleCourseInputChange}
                      placeholder={activeTab === "individual" ? "Individual driving lessons..." : "Practical driving lessons for beginners..."}
                      className="h-20"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="subtype">Course Subtype</Label>
                    <Select
                      name="subtype"
                      value={newCourse.subtype}
                      onValueChange={(value) => handleCourseSelectChange({ target: { name: "subtype", value } } as any)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select course subtype" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="practical">Practical Course</SelectItem>
                        <SelectItem value="individual">Individual Lessons</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {newCourse.subtype === "practical" && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="price">Base Price</Label>
                        <Input
                          type="number"
                          name="price"
                          value={newCourse.price}
                          onChange={handleCourseSelectChange}
                          placeholder="Enter base price"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="duration">Duration (days)</Label>
                        <Input
                          type="number"
                          name="duration"
                          value={newCourse.duration}
                          onChange={handleCourseSelectChange}
                          placeholder="Enter duration in days"
                        />
                      </div>
                    </div>
                  )}
                  
                  {newCourse.subtype === "individual" && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="pricePerHour">Rate per Hour (SAR)</Label>
                          <Input
                            id="pricePerHour"
                            type="number"
                            min="0"
                            step="0.01"
                            name="pricePerHour"
                            value={newCourse.pricePerHour || ""}
                            onChange={handleCourseSelectChange}
                            placeholder="Enter rate per hour"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="totalHours">Total Hours</Label>
                          <Input
                            id="totalHours"
                            type="number"
                            min="1"
                            name="totalHours"
                            value={newCourse.totalHours || ""}
                            onChange={handleCourseSelectChange}
                            placeholder="Enter total hours"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <Label htmlFor="thumbnail">Thumbnail URL</Label>
                    <Input
                      id="thumbnail"
                      name="thumbnail"
                      value={newCourse.thumbnail || ""}
                      onChange={handleCourseInputChange}
                      placeholder="/images/course-thumbnail.jpg"
                    />
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="published"
                      name="published"
                      checked={newCourse.published}
                      onChange={(e) => setNewCourse({...newCourse, published: e.target.checked})}
                      className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    />
                    <Label htmlFor="published">Publish immediately</Label>
                  </div>
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => {
                    setIsAddCourseDialogOpen(false);
                    setSelectedCourse(null);
                    setNewCourse({
                      title: "",
                      description: "",
                      type: "practical",
                      subtype: activeTab,
                      price: activeTab === "individual" ? undefined : 199.99,
                      pricePerHour: activeTab === "individual" ? 45.99 : undefined,
                      duration: activeTab === "individual" ? undefined : 10,
                      published: true,
                      contents: []
                    });
                  }}>
                    Cancel
                  </Button>
                  <Button onClick={handleAddCourse} disabled={isCreating}>
                    {isCreating ? (
                      <>
                        <span className="mr-2">{selectedCourse ? 'Updating...' : 'Creating...'}</span>
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                      </>
                    ) : (
                      selectedCourse ? 'Update Course' : 'Add Course'
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {loading && filteredCourses.length === 0 ? (
            <div className="flex justify-center items-center h-64">
              <p className="text-gray-500">Loading courses...</p>
            </div>
          ) : (
            <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
              {filteredCourses.length > 0 ? (
                filteredCourses.map((course) => (
                  <Card key={course.id} className="overflow-hidden">
                    <div className="h-40 bg-gray-100 relative">
                      {course.thumbnail && (
                        <img
                          src={course.thumbnail}
                          alt={course.title}
                          className="w-full h-full object-cover"
                        />
                      )}
                      <div className="absolute top-2 right-2 flex gap-2">
                        <Badge 
                          className={course.published ? "bg-green-500" : "bg-gray-500"}
                        >
                          {course.published ? "Published" : "Draft"}
                        </Badge>
                        <Badge variant="outline" className="bg-white">
                          {course.subtype === "individual" ? "Individual Lesson" : "Practical Course"}
                        </Badge>
                      </div>
                    </div>
                    <CardHeader className="pb-2">
                      <div className="flex justify-between items-start">
                        <CardTitle className="text-lg">{course.title}</CardTitle>
                      </div>
                      <div className="flex items-center mt-1">
                        <DollarSign className="h-4 w-4 text-green-600 mr-1" />
                        <span className="font-semibold">
                          {course.subtype === "individual" 
                            ? `£${Number(course.pricePerHour || 0).toFixed(2)}/hour`
                            : `£${Number(course.price || 0).toFixed(2)}`}
                        </span>
                        {course.subtype === "practical" && course.duration && (
                          <span className="ml-2 text-sm text-gray-500">
                            {course.duration} days
                          </span>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="pb-3">
                      <p className="text-sm text-gray-600 line-clamp-3 mb-4">
                        {course.description}
                      </p>
                      
                      <div className="flex justify-between items-center mt-4">
                        <div className="text-xs text-gray-500">
                          Updated: {new Date(course.updatedAt).toLocaleDateString()}
                        </div>
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => navigate(`/admin-dashboard/course-preview/${course.id}`)}
                            disabled={loading}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => handleEditCourse(course)}
                            disabled={loading}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => handleTogglePublishCourse(course.id, course.published)}
                            disabled={loading}
                          >
                            <Book className={`h-4 w-4 ${course.published ? "text-green-600" : "text-gray-400"}`} />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleDeleteCourse(course.id)}
                            disabled={loading}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <div className="col-span-full flex flex-col items-center justify-center py-12 text-center">
                  <Calendar className="h-12 w-12 text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900">No courses found</h3>
                  <p className="text-gray-500 mt-2 max-w-md">
                    No practical courses match your search criteria. Try adjusting your search or add new courses.
                  </p>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="individual" className="space-y-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center w-full max-w-sm space-x-2">
              <Input
                placeholder="Search courses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1"
              />
              <Button variant="outline" size="icon">
                <Search className="h-4 w-4" />
              </Button>
            </div>

            <Dialog open={isAddCourseDialogOpen} onOpenChange={setIsAddCourseDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={handleOpenAddCourseDialog}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Individual Course
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-6">
                <DialogHeader>
                  <DialogTitle>Add Individual Course</DialogTitle>
                </DialogHeader>
                
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="title">Title*</Label>
                      <Input
                        id="title"
                        name="title"
                        value={newCourse.title}
                        onChange={handleCourseInputChange}
                        placeholder="Individual Driving Lesson"
                        className="w-full"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="type">Course Type</Label>
                      <Select
                        name="type"
                        value="practical"
                        onValueChange={(value) => handleCourseSelectChange({ target: { name: "type", value: "practical" } } as any)}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select course type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="practical">Practical</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Description*</Label>
                    <Textarea
                      id="description"
                      name="description"
                      value={newCourse.description}
                      onChange={handleCourseInputChange}
                      placeholder="Individual driving lessons..."
                      className="h-20 w-full"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="pricePerHour">Rate per Hour (SAR)*</Label>
                      <Input
                        id="pricePerHour"
                        type="number"
                        min="0"
                        step="0.01"
                        name="pricePerHour"
                        value={newCourse.pricePerHour || ""}
                        onChange={handleCourseSelectChange}
                        placeholder="Enter rate per hour"
                        className="w-full"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="totalHours">Total Hours</Label>
                      <Input
                        id="totalHours"
                        type="number"
                        min="1"
                        name="totalHours"
                        value={newCourse.totalHours || ""}
                        onChange={handleCourseSelectChange}
                        placeholder="Enter total hours"
                        className="w-full"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="thumbnail">Thumbnail URL</Label>
                    <Input
                      id="thumbnail"
                      name="thumbnail"
                      value={newCourse.thumbnail || ""}
                      onChange={handleCourseInputChange}
                      placeholder="/images/course-thumbnail.jpg"
                      className="w-full"
                    />
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="published"
                      name="published"
                      checked={newCourse.published}
                      onChange={(e) => setNewCourse({...newCourse, published: e.target.checked})}
                      className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    />
                    <Label htmlFor="published">Publish immediately</Label>
                  </div>
                </div>

                <DialogFooter className="mt-6">
                  <Button variant="outline" onClick={() => setIsAddCourseDialogOpen(false)} disabled={isCreating}>
                    Cancel
                  </Button>
                  <Button onClick={handleAddCourse} disabled={isCreating}>
                    {isCreating ? (
                      <>
                        <span className="mr-2">Creating...</span>
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                      </>
                    ) : (
                      "Add Course"
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {loading && filteredCourses.length === 0 ? (
            <div className="flex justify-center items-center h-64">
              <p className="text-gray-500">Loading courses...</p>
            </div>
          ) : (
            <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
              {filteredCourses.length > 0 ? (
                filteredCourses.map((course) => (
                  <Card key={course.id} className="overflow-hidden">
                    <div className="h-40 bg-gray-100 relative">
                      {course.thumbnail && (
                        <img
                          src={course.thumbnail}
                          alt={course.title}
                          className="w-full h-full object-cover"
                        />
                      )}
                      <div className="absolute top-2 right-2 flex gap-2">
                        <Badge 
                          className={course.published ? "bg-green-500" : "bg-gray-500"}
                        >
                          {course.published ? "Published" : "Draft"}
                        </Badge>
                        <Badge variant="outline" className="bg-white">
                          Individual
                        </Badge>
                      </div>
                    </div>
                    <CardHeader className="pb-2">
                      <div className="flex justify-between items-start">
                        <CardTitle className="text-lg">{course.title}</CardTitle>
                      </div>
                      <div className="flex items-center mt-1">
                        <DollarSign className="h-4 w-4 text-green-600 mr-1" />
                        <span className="font-semibold">
                          {course.subtype === "individual" 
                            ? `£${Number(course.pricePerHour || 0).toFixed(2)}/hour`
                            : `£${Number(course.price || 0).toFixed(2)}`}
                        </span>
                        {course.duration && (
                          <span className="ml-2 text-sm text-gray-500">
                            {course.duration} {course.type === "theory" ? "days" : "hours"}
                          </span>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="pb-3">
                      <p className="text-sm text-gray-600 line-clamp-3 mb-4">
                        {course.description}
                      </p>
                      
                      <div className="flex justify-between items-center mt-4">
                        <div className="text-xs text-gray-500">
                          Updated: {new Date(course.updatedAt).toLocaleDateString()}
                        </div>
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => navigate(`/admin-dashboard/course-preview/${course.id}`)}
                            disabled={loading}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleEditCourse(course)}
                            disabled={loading}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => handleTogglePublishCourse(course.id, course.published)}
                            disabled={loading}
                          >
                            <Book className={`h-4 w-4 ${course.published ? "text-green-600" : "text-gray-400"}`} />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleDeleteCourse(course.id)}
                            disabled={loading}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <div className="col-span-full flex flex-col items-center justify-center py-12 text-center">
                  <Book className="h-12 w-12 text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900">No courses found</h3>
                  <p className="text-gray-500 mt-2 max-w-md">
                    No individual courses match your search criteria. Try adjusting your search or add new courses.
                  </p>
                </div>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the course
              and remove all associated content from our servers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDeleteCourse}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete Course
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
};

export default CourseManagement;
